/**
 * MCP Query Tool Handlers
 *
 * Implementations for search and analysis tools.
 */

import type { IDFObject } from '../../parser';
import type { MCPToolResult, SearchResult } from '../types';
import type { HandlerContext } from './readHandlers';

/**
 * Get object name from first field
 */
function getObjectName(obj: IDFObject): string | null {
  return obj.fields[0]?.value || null;
}

/**
 * Calculate search relevance score
 */
function calculateScore(obj: IDFObject, query: string): number {
  const lowerQuery = query.toLowerCase();
  let score = 0;

  const name = getObjectName(obj)?.toLowerCase() || '';
  const className = obj.className.toLowerCase();

  // Exact name match
  if (name === lowerQuery) score += 100;
  // Name starts with query
  else if (name.startsWith(lowerQuery)) score += 50;
  // Name contains query
  else if (name.includes(lowerQuery)) score += 25;

  // Class name match
  if (className === lowerQuery) score += 80;
  else if (className.includes(lowerQuery)) score += 20;

  // Field value matches
  for (const field of obj.fields) {
    const value = field.value.toLowerCase();
    if (value === lowerQuery) score += 15;
    else if (value.includes(lowerQuery)) score += 5;
  }

  return score;
}

/**
 * Handle search_objects tool
 */
export function handleSearchObjects(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const query = args["query"] as string;
  const limit = (args["limit"] as number) || 20;
  const classFilter = args["classFilter"] as string | undefined;

  let objects = ctx.document.objects;

  // Apply class filter
  if (classFilter) {
    objects = objects.filter(
      (obj) => obj.className.toLowerCase() === classFilter.toLowerCase()
    );
  }

  // Score and sort by relevance
  const scored: Array<{ obj: IDFObject; score: number }> = objects
    .map((obj) => ({ obj, score: calculateScore(obj, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  const results: SearchResult[] = scored.map(({ obj, score }) => {
    // Find the best matching field
    const lowerQuery = query.toLowerCase();
    let matchedField: { index: number; name: string; value: string } | null = null;

    for (let i = 0; i < obj.fields.length; i++) {
      const value = obj.fields[i]?.value || '';
      if (value.toLowerCase().includes(lowerQuery)) {
        const schemaType = ctx.schema?.objectTypes.get(obj.className.toLowerCase());
        matchedField = {
          index: i,
          name: schemaType?.fields[i]?.name || `Field ${String(i + 1)}`,
          value,
        };
        break;
      }
    }

    const result: SearchResult = {
      objectId: obj.id,
      className: obj.className,
      objectName: getObjectName(obj),
      score,
    };
    if (matchedField) {
      result.matchedField = matchedField;
    }
    return result;
  });

  return {
    success: true,
    content: {
      query,
      resultCount: results.length,
      results,
    },
  };
}

/**
 * Handle find_references tool
 */
export function handleFindReferences(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectName = args["objectName"] as string;
  const lowerName = objectName.toLowerCase();

  const references: Array<{
    objectId: string;
    className: string;
    objectName: string | null;
    fieldIndex: number;
    fieldName: string;
  }> = [];

  for (const obj of ctx.document.objects) {
    const schemaType = ctx.schema?.objectTypes.get(obj.className.toLowerCase());

    for (let i = 0; i < obj.fields.length; i++) {
      const fieldValue = obj.fields[i]?.value;
      if (fieldValue && fieldValue.toLowerCase() === lowerName) {
        references.push({
          objectId: obj.id,
          className: obj.className,
          objectName: getObjectName(obj),
          fieldIndex: i,
          fieldName: schemaType?.fields[i]?.name || `Field ${String(i + 1)}`,
        });
      }
    }
  }

  return {
    success: true,
    content: {
      referencedName: objectName,
      referenceCount: references.length,
      references,
    },
  };
}

/**
 * Handle find_objects_with_value tool
 */
export function handleFindObjectsWithValue(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const value = args["value"] as string;
  const exactMatch = (args["exactMatch"] as boolean) || false;
  const lowerValue = value.toLowerCase();

  const matches: Array<{
    objectId: string;
    className: string;
    objectName: string | null;
    fieldIndex: number;
    fieldName: string;
    fieldValue: string;
  }> = [];

  for (const obj of ctx.document.objects) {
    const schemaType = ctx.schema?.objectTypes.get(obj.className.toLowerCase());

    for (let i = 0; i < obj.fields.length; i++) {
      const fieldValue = obj.fields[i]?.value || '';
      const lowerFieldValue = fieldValue.toLowerCase();

      const isMatch = exactMatch
        ? lowerFieldValue === lowerValue
        : lowerFieldValue.includes(lowerValue);

      if (isMatch) {
        matches.push({
          objectId: obj.id,
          className: obj.className,
          objectName: getObjectName(obj),
          fieldIndex: i,
          fieldName: schemaType?.fields[i]?.name || `Field ${String(i + 1)}`,
          fieldValue,
        });
      }
    }
  }

  return {
    success: true,
    content: {
      searchValue: value,
      exactMatch,
      matchCount: matches.length,
      matches: matches.slice(0, 100),
    },
  };
}

/**
 * Handle get_objects_by_zone tool
 */
export function handleGetObjectsByZone(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const zoneName = args["zoneName"] as string;
  const lowerZoneName = zoneName.toLowerCase();

  // Categories of zone-related objects
  const surfaces: Array<{ id: string; className: string; name: string | null }> = [];
  const fenestration: Array<{ id: string; className: string; name: string | null }> = [];
  const people: Array<{ id: string; className: string; name: string | null }> = [];
  const lights: Array<{ id: string; className: string; name: string | null }> = [];
  const equipment: Array<{ id: string; className: string; name: string | null }> = [];
  const infiltration: Array<{ id: string; className: string; name: string | null }> = [];
  const hvacEquipment: Array<{ id: string; className: string; name: string | null }> = [];
  const other: Array<{ id: string; className: string; name: string | null }> = [];

  for (const obj of ctx.document.objects) {
    const lowerClass = obj.className.toLowerCase();

    // Check if this object references the zone
    let referencesZone = false;
    for (const field of obj.fields) {
      if (field.value.toLowerCase() === lowerZoneName) {
        referencesZone = true;
        break;
      }
    }

    if (!referencesZone) continue;

    const info = {
      id: obj.id,
      className: obj.className,
      name: getObjectName(obj),
    };

    // Categorize
    if (lowerClass.includes('surface')) {
      surfaces.push(info);
    } else if (lowerClass.includes('fenestration') || lowerClass.includes('window') || lowerClass.includes('door')) {
      fenestration.push(info);
    } else if (lowerClass === 'people') {
      people.push(info);
    } else if (lowerClass.startsWith('lights')) {
      lights.push(info);
    } else if (lowerClass.includes('equipment') && !lowerClass.includes('hvac')) {
      equipment.push(info);
    } else if (lowerClass.includes('infiltration') || lowerClass.includes('ventilation')) {
      infiltration.push(info);
    } else if (lowerClass.startsWith('zonehvac:') || lowerClass.includes('hvac')) {
      hvacEquipment.push(info);
    } else {
      other.push(info);
    }
  }

  const categories = { surfaces, fenestration, people, lights, equipment, infiltration, hvacEquipment, other };

  const totalCount = Object.values(categories).reduce((sum, arr) => sum + arr.length, 0);

  return {
    success: true,
    content: {
      zoneName,
      totalObjects: totalCount,
      categories,
    },
  };
}

/**
 * Handle get_connected_objects tool
 */
export function handleGetConnectedObjects(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const depth = (args["depth"] as number) || 1;

  const startObj = ctx.getObjectById(objectId);
  if (!startObj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const visited = new Set<string>();
  const connections: Array<{
    sourceObjectId: string;
    sourceObjectName: string | null;
    sourceClassName: string;
    targetObjectId: string;
    targetObjectName: string | null;
    targetClassName: string;
    connectionField: string;
    direction: 'outgoing' | 'incoming';
  }> = [];

  function findConnections(obj: IDFObject, currentDepth: number) {
    if (currentDepth > depth || visited.has(obj.id)) return;
    visited.add(obj.id);

    const objName = getObjectName(obj);
    const schemaType = ctx.schema?.objectTypes.get(obj.className.toLowerCase());

    // Find outgoing references (this object references others)
    for (let i = 0; i < obj.fields.length; i++) {
      const fieldValue = obj.fields[i]?.value;
      if (!fieldValue) continue;

      // Look for objects with matching names
      const doc = ctx.document;
      if (!doc) continue;
      const referencedObj = doc.objects.find(
        (o) => o.id !== obj.id && getObjectName(o)?.toLowerCase() === fieldValue.toLowerCase()
      );

      if (referencedObj) {
        connections.push({
          sourceObjectId: obj.id,
          sourceObjectName: objName,
          sourceClassName: obj.className,
          targetObjectId: referencedObj.id,
          targetObjectName: getObjectName(referencedObj),
          targetClassName: referencedObj.className,
          connectionField: schemaType?.fields[i]?.name || `Field ${String(i + 1)}`,
          direction: 'outgoing',
        });

        if (currentDepth < depth) {
          findConnections(referencedObj, currentDepth + 1);
        }
      }
    }

    // Find incoming references (other objects reference this one)
    if (objName && ctx.document) {
      const lowerName = objName.toLowerCase();
      for (const otherObj of ctx.document.objects) {
        if (otherObj.id === obj.id || visited.has(otherObj.id)) continue;

        const otherSchemaType = ctx.schema?.objectTypes.get(otherObj.className.toLowerCase());

        for (let i = 0; i < otherObj.fields.length; i++) {
          if (otherObj.fields[i]?.value.toLowerCase() === lowerName) {
            connections.push({
              sourceObjectId: otherObj.id,
              sourceObjectName: getObjectName(otherObj),
              sourceClassName: otherObj.className,
              targetObjectId: obj.id,
              targetObjectName: objName,
              targetClassName: obj.className,
              connectionField: otherSchemaType?.fields[i]?.name || `Field ${String(i + 1)}`,
              direction: 'incoming',
            });

            if (currentDepth < depth) {
              findConnections(otherObj, currentDepth + 1);
            }
            break;
          }
        }
      }
    }
  }

  findConnections(startObj, 1);

  return {
    success: true,
    content: {
      startObject: {
        id: objectId,
        className: startObj.className,
        name: getObjectName(startObj),
      },
      depth,
      connectionCount: connections.length,
      connections,
    },
  };
}

/**
 * Handle compare_objects tool
 */
export function handleCompareObjects(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId1 = args["objectId1"] as string;
  const objectId2 = args["objectId2"] as string;

  const obj1 = ctx.getObjectById(objectId1);
  const obj2 = ctx.getObjectById(objectId2);

  if (!obj1) return { success: false, error: `Object not found: ${objectId1}` };
  if (!obj2) return { success: false, error: `Object not found: ${objectId2}` };

  if (obj1.className.toLowerCase() !== obj2.className.toLowerCase()) {
    return {
      success: false,
      error: `Cannot compare objects of different classes: ${obj1.className} vs ${obj2.className}`,
    };
  }

  const schemaType = ctx.schema?.objectTypes.get(obj1.className.toLowerCase());
  const maxFields = Math.max(obj1.fields.length, obj2.fields.length);

  const differences: Array<{
    fieldIndex: number;
    fieldName: string;
    value1: string;
    value2: string;
  }> = [];

  const identical: Array<{
    fieldIndex: number;
    fieldName: string;
    value: string;
  }> = [];

  for (let i = 0; i < maxFields; i++) {
    const val1 = obj1.fields[i]?.value || '';
    const val2 = obj2.fields[i]?.value || '';
    const fieldName = schemaType?.fields[i]?.name || `Field ${String(i + 1)}`;

    if (val1 !== val2) {
      differences.push({ fieldIndex: i, fieldName, value1: val1, value2: val2 });
    } else if (val1) {
      identical.push({ fieldIndex: i, fieldName, value: val1 });
    }
  }

  return {
    success: true,
    content: {
      className: obj1.className,
      object1: { id: objectId1, name: getObjectName(obj1) },
      object2: { id: objectId2, name: getObjectName(obj2) },
      differenceCount: differences.length,
      identicalCount: identical.length,
      differences,
      identical,
    },
  };
}

/**
 * Handle get_objects_with_errors tool
 */
export function handleGetObjectsWithErrors(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  // This would integrate with the validation system
  // For now, return a placeholder that indicates validation should be run
  return {
    success: true,
    content: {
      message: 'Use validate_model to get objects with errors',
      suggestion: 'Run validate_model first, then use the results to identify problematic objects',
    },
  };
}

/**
 * Handle get_orphaned_objects tool
 */
export function handleGetOrphanedObjects(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const classFilter = args["classFilter"] as string | undefined;

  // Build a set of all referenced names
  const referencedNames = new Set<string>();
  for (const obj of ctx.document.objects) {
    for (const field of obj.fields) {
      if (field.value) {
        referencedNames.add(field.value.toLowerCase());
      }
    }
  }

  // Find objects that are not referenced
  const orphaned: Array<{
    id: string;
    className: string;
    name: string | null;
  }> = [];

  for (const obj of ctx.document.objects) {
    if (classFilter && obj.className.toLowerCase() !== classFilter.toLowerCase()) {
      continue;
    }

    const name = getObjectName(obj);
    if (!name) continue;

    // Skip checking if the object's name appears in any field
    // (excluding its own definition)
    const lowerName = name.toLowerCase();
    let isReferenced = false;

    for (const otherObj of ctx.document.objects) {
      if (otherObj.id === obj.id) continue;

      for (const field of otherObj.fields) {
        if (field.value.toLowerCase() === lowerName) {
          isReferenced = true;
          break;
        }
      }
      if (isReferenced) break;
    }

    if (!isReferenced) {
      orphaned.push({
        id: obj.id,
        className: obj.className,
        name,
      });
    }
  }

  return {
    success: true,
    content: {
      classFilter: classFilter || 'all',
      orphanedCount: orphaned.length,
      orphaned: orphaned.slice(0, 100),
    },
  };
}

/**
 * Handle get_duplicate_objects tool
 */
export function handleGetDuplicateObjects(
  _args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const nameToObjects = new Map<string, Array<{ id: string; className: string }>>();

  for (const obj of ctx.document.objects) {
    const name = getObjectName(obj);
    if (!name) continue;

    const key = name.toLowerCase();
    const existing = nameToObjects.get(key) || [];
    existing.push({ id: obj.id, className: obj.className });
    nameToObjects.set(key, existing);
  }

  const duplicates: Array<{
    name: string;
    count: number;
    objects: Array<{ id: string; className: string }>;
  }> = [];

  for (const [name, objects] of nameToObjects.entries()) {
    if (objects.length > 1) {
      duplicates.push({
        name,
        count: objects.length,
        objects,
      });
    }
  }

  return {
    success: true,
    content: {
      duplicateCount: duplicates.length,
      duplicates,
    },
  };
}

/**
 * Handle filter_objects tool
 */
export function handleFilterObjects(
  args: Record<string, unknown>,
  ctx: HandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const className = args["className"] as string;
  const conditions = args["conditions"] as Array<{
    fieldIndex: number;
    operator: string;
    value: string;
    value2?: string;
  }>;

  let objects = ctx.document.objects.filter(
    (obj) => obj.className.toLowerCase() === className.toLowerCase()
  );

  // Apply conditions
  for (const condition of conditions) {
    objects = objects.filter((obj) => {
      const fieldValue = obj.fields[condition.fieldIndex]?.value || '';
      const compareValue = condition.value;

      switch (condition.operator) {
        case 'equals':
          return fieldValue.toLowerCase() === compareValue.toLowerCase();
        case 'notEquals':
          return fieldValue.toLowerCase() !== compareValue.toLowerCase();
        case 'contains':
          return fieldValue.toLowerCase().includes(compareValue.toLowerCase());
        case 'greaterThan': {
          const numField = parseFloat(fieldValue);
          const numCompare = parseFloat(compareValue);
          return !isNaN(numField) && !isNaN(numCompare) && numField > numCompare;
        }
        case 'lessThan': {
          const numField = parseFloat(fieldValue);
          const numCompare = parseFloat(compareValue);
          return !isNaN(numField) && !isNaN(numCompare) && numField < numCompare;
        }
        case 'between': {
          const numField = parseFloat(fieldValue);
          const numMin = parseFloat(compareValue);
          const numMax = parseFloat(condition.value2 || '0');
          return !isNaN(numField) && !isNaN(numMin) && !isNaN(numMax) &&
                 numField >= numMin && numField <= numMax;
        }
        default:
          return true;
      }
    });
  }

  const schemaType = ctx.schema?.objectTypes.get(className.toLowerCase());

  return {
    success: true,
    content: {
      className,
      conditions,
      matchCount: objects.length,
      objects: objects.slice(0, 50).map((obj) => ({
        id: obj.id,
        name: getObjectName(obj),
        fields: obj.fields.map((f, i) => ({
          index: i,
          name: schemaType?.fields[i]?.name || `Field ${String(i + 1)}`,
          value: f.value,
        })),
      })),
    },
  };
}

/**
 * All query handlers
 */
export const queryHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: HandlerContext) => MCPToolResult
> = {
  search_objects: handleSearchObjects,
  find_references: handleFindReferences,
  find_objects_with_value: handleFindObjectsWithValue,
  get_objects_by_zone: handleGetObjectsByZone,
  get_connected_objects: handleGetConnectedObjects,
  compare_objects: handleCompareObjects,
  get_objects_with_errors: handleGetObjectsWithErrors,
  get_orphaned_objects: handleGetOrphanedObjects,
  get_duplicate_objects: handleGetDuplicateObjects,
  filter_objects: handleFilterObjects,
};
